import React from "react";
import {
  Box,
  Drawer,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SignOut from "./SignOut";
import AppBarStyled from "./AppBarStyled";
import Main from "./Main";
import DrawerHeader from "./DrawerHeader";
import LogoImage from "./LogoImage";
import LibraryIcon from "@mui/icons-material/LibraryBooks";
import StorageIcon from "@mui/icons-material/Storage";
import strings from "../strings";

const drawerWidth = 300;

const Layout = ({ children }) => {
  const [open, setOpen] = React.useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/data-entry-form") return strings.layout.pageTitles.dataEntryForm;
    if (path === "/library") return strings.layout.pageTitles.library;
    if (path === "/database") return strings.layout.pageTitles.database;
    if (path === "/") return strings.layout.pageTitles.home;
    return "";
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    
    const menuButton = document.querySelector('[aria-label="open drawer"]');
    if (menuButton) {
      menuButton.focus();
    }
  };

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const menuItems = [
    { text: strings.layout.menuItems.home, icon: <DashboardIcon />, path: "/" },
    {
      text: strings.layout.menuItems.dataEntryForm,
      icon: <DescriptionIcon />,
      path: "/data-entry-form",
    },
    { text: strings.layout.menuItems.library, icon: <LibraryIcon />, path: "/library" },
    { text: strings.layout.menuItems.database, icon: <StorageIcon />, path: "/database" },
  ];

  const externalLinks = [
    {
      text: strings.layout.externalLinks.portWebsite,
      icon: <OpenInNewIcon />,
      url: "https://www.portofoakland.com/",
    },
    {
      text: strings.layout.externalLinks.portWideEDMS,
      icon: <OpenInNewIcon />,
      url: "https://trccompanies.sharepoint.com/sites/CS-P3/Port-of-Oakland/SitePages/Home.aspx",
    },
    {
      text: strings.layout.externalLinks.validValues,
      icon: <OpenInNewIcon />,
      url: "https://trccompanies.sharepoint.com/sites/CS-P3/Port-of-Oakland/Lists/Data%20Entry_Valid%20Values/AllItems.aspx",
    },
    {
      text: strings.layout.externalLinks.abbreviations,
      icon: <OpenInNewIcon />,
      url: "https://trccompanies.sharepoint.com/sites/CS-P3/Port-of-Oakland/Lists/Data%20Entry_Abbreviations/AllItems.aspx",
    },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      <AppBarStyled position="fixed" open={!isMobile && open}>
        <Toolbar>
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(!isMobile && open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: 'primary.main',
              textAlign: 'left'
            }}
          >
            {getPageTitle()}
          </Typography>
          <SignOut />
        </Toolbar>
      </AppBarStyled>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={isMobile ? handleDrawerClose : undefined}
        ModalProps={{
          keepMounted: true,
          disableAutoFocus: true,
          disableEnforceFocus: true,
        }}
      >
        <DrawerHeader>
          <LogoImage
            src="https://www.portofoakland.com/wp-content/uploads/2023/12/Port-Logo.svg"
            alt="Port of Oakland Logo"
          />
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton 
                onClick={() => handleNavigation(item.path)}
                sx={{
                  backgroundColor: location.pathname === item.path ? 'primary.main' : 'transparent',
                  color: location.pathname === item.path ? 'primary.contrastText' : 'inherit',
                  '&:hover': {
                    backgroundColor: location.pathname === item.path ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.contrastText' : 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          {externalLinks.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Main open={!isMobile && open}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
};

export default Layout;
